import React, { useState, useEffect } from 'react';
import { templatesAPI } from '../services/api';
import './TemplateSelector.css';

const TemplateSelector = ({ type = 'email', buyerData, onSelect, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '' });
  const [editableSubject, setEditableSubject] = useState('');
  const [editableBody, setEditableBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, [type]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await templatesAPI.getAll({ type });
      // Handle different response structures
      const templateData = response.data?.templates || response.data || [];
      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (err) {
      setError('Failed to load templates');
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const replaceVariables = (text) => {
    if (!text || !buyerData) return text;

    let result = text;
    const variables = {
      '{business_name}': buyerData.business_name || '',
      '{first_name}': extractFirstName(buyerData.business_name) || '',
      '{location}': buyerData.location || '',
      '{industry}': buyerData.industry || '',
      '{email}': buyerData.email || '',
      '{phone}': buyerData.phone || '',
      '{website}': buyerData.website || ''
    };

    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(key, 'g'), value);
    });

    return result;
  };

  const extractFirstName = (businessName) => {
    if (!businessName) return '';
    // Try to extract a first name from business name
    const words = businessName.split(' ');
    return words[0];
  };

  const highlightVariables = (text) => {
    if (!text) return '';

    const variablePattern = /\{[^}]+\}/g;
    return text.split(variablePattern).reduce((acc, part, index, array) => {
      acc.push(part);
      if (index < array.length - 1) {
        const match = text.match(variablePattern)[index];
        acc.push(<span key={index} className="variable-highlight">{match}</span>);
      }
      return acc;
    }, []);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);

    // Parse the template content
    // Try to extract subject from content if not separate field
    let subject = '';
    let body = template.content || '';

    // Check if content has "Subject:" line
    if (body.includes('Subject:')) {
      const lines = body.split('\n');
      const subjectLine = lines.find(line => line.trim().startsWith('Subject:'));
      if (subjectLine) {
        subject = subjectLine.replace(/^Subject:\s*/i, '').trim();
        // Remove subject line from body
        body = lines.filter(line => !line.trim().startsWith('Subject:')).join('\n').trim();
      }
    }

    // Replace variables for preview
    const previewSubject = replaceVariables(subject);
    const previewBody = replaceVariables(body);

    setPreviewContent({ subject: previewSubject, body: previewBody });
    setEditableSubject(previewSubject);
    setEditableBody(previewBody);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelect({
        subject: editableSubject,
        message: editableBody,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name
      });
      onClose();
    }
  };

  const renderVariableKey = () => {
    const availableVariables = [
      { key: '{business_name}', desc: 'Buyer business name', value: buyerData?.business_name },
      { key: '{first_name}', desc: 'First name from business', value: extractFirstName(buyerData?.business_name) },
      { key: '{location}', desc: 'Buyer location', value: buyerData?.location },
      { key: '{industry}', desc: 'Buyer industry', value: buyerData?.industry },
      { key: '{email}', desc: 'Buyer email', value: buyerData?.email },
      { key: '{phone}', desc: 'Buyer phone', value: buyerData?.phone },
      { key: '{website}', desc: 'Buyer website', value: buyerData?.website }
    ];

    return (
      <div className="variable-key">
        <h4>Available Variables</h4>
        <div className="variable-list">
          {availableVariables.map(v => (
            <div key={v.key} className="variable-item">
              <code className="variable-code">{v.key}</code>
              <span className="variable-desc">{v.desc}</span>
              {v.value && <span className="variable-value">→ {v.value}</span>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="template-selector">
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content template-modal" onClick={e => e.stopPropagation()}>
            <div className="loading">Loading templates...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="template-selector">
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content template-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Select Template</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="template-selector-body">
            {/* Left Panel - Template List */}
            <div className="template-list-panel">
              <h3>Templates ({templates.length})</h3>
              {templates.length === 0 ? (
                <div className="no-templates">
                  <p>No templates found.</p>
                  <p>Create a template from the Templates page first.</p>
                </div>
              ) : (
                <div className="template-list">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className={`template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="template-item-header">
                        <h4>{template.name}</h4>
                        {template.industry && (
                          <span className="template-industry">{template.industry}</span>
                        )}
                      </div>
                      <div className="template-preview-text">
                        {template.content.substring(0, 100)}...
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Panel - Preview and Edit */}
            {selectedTemplate ? (
              <div className="template-preview-panel">
                <h3>Preview & Edit</h3>

                {renderVariableKey()}

                <div className="preview-section">
                  <div className="form-group">
                    <label>Subject</label>
                    <input
                      type="text"
                      value={editableSubject}
                      onChange={(e) => setEditableSubject(e.target.value)}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      value={editableBody}
                      onChange={(e) => setEditableBody(e.target.value)}
                      className="form-control message-textarea"
                      rows={15}
                    />
                  </div>

                  <div className="preview-info">
                    <p>Variables have been replaced with actual buyer data. You can still edit the content above before sending.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="template-preview-panel empty">
                <div className="empty-state">
                  <p>Select a template from the list to preview</p>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUseTemplate}
              disabled={!selectedTemplate}
            >
              Use This Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;
